﻿/**
 * Fragments Class
 * Handles the public Fragment module API and delegates implementation
 * details to focused internal managers.
 */

import type { Fragment, FragmentName, FragmentStatus, PaymentMethod } from '@anddone/ajs-util';
import { createLogger } from '@anddone/ajs-logger';

import type { Events } from './Events';
import type { Orchestrator } from './Orchestrator';
import type { StateManagement } from './StateManagement';

import { FragmentRegistry } from './fragment-registry';
import { FragmentMountManager } from './fragment-module/FragmentMountManager';
import { FragmentLifecycleManager } from './fragment-module/FragmentLifecycleManager';
import { FragmentCommandManager } from './fragment-module/FragmentCommandManager';
import { FragmentMessageHandler } from './fragment-module/FragmentMessageHandler';

const logger = createLogger('SDK', 'Fragments');

export class Fragments {
  private readonly dataState: StateManagement;
  private readonly registry: FragmentRegistry;
  private readonly lifecycle: FragmentLifecycleManager;
  private readonly mounting: FragmentMountManager;
  private readonly commands: FragmentCommandManager;
  private readonly messages: FragmentMessageHandler;

  /**
   * constructor
   *
   * @param dataState - Shared config and SDK state
   * @param orchestrator - Reference to orchestrator module
   * @param events - Reference to events module
   * @param fragmentRegistry - Reference to fragmentRegistry module
   */
  constructor(
    dataState: StateManagement,
    orchestrator: Orchestrator,
    events: Events,
    fragmentRegistry: FragmentRegistry,
  ) {
    this.dataState = dataState;
    this.registry = fragmentRegistry;

    this.lifecycle = new FragmentLifecycleManager(
      dataState,
      orchestrator,
      events,
      fragmentRegistry,
    );
    this.commands = new FragmentCommandManager(
      dataState,
      orchestrator,
      events,
      fragmentRegistry,
      this.lifecycle,
    );
    dataState.bindCommandDispatcher(this.commands);
    this.mounting = new FragmentMountManager(dataState, orchestrator, events, fragmentRegistry);
    this.messages = new FragmentMessageHandler(
      dataState,
      orchestrator,
      events,
      fragmentRegistry,
      this.lifecycle,
      this.commands,
    );

    // Register message handler with orchestrator
    orchestrator.onMessage(message => this.messages.handleMessage(message));

    this.mounting.mountOrchestratorFragment();

    logger.debug('init', 'Fragment module loaded');
  }

  /**
   * Mount all consumer-configured fragments.
   * Called by the SDK after the settings endpoint has responded successfully.
   *
   * Idempotent — returns early once the SDK has been marked initialized to
   * prevent duplicate mounting if called more than once.
   *
   * @returns void
   */
  public mountConfiguredFragments(): void {
    this.mounting.mountAllFragments();
  }

  /**
   * Get a specific fragment's references.
   *
   * @internal Use AndDone.getFragment() instead.
   * @param fragmentName - Name of the fragment type (e.g., 'card', 'cvv', etc.)
   * @returns Fragment | null
   */
  public getFragment(fragmentName: FragmentName): Fragment | null {
    return this.registry.get(fragmentName);
  }

  /**
   * Check if a fragment is staged.
   *
   * A submitted fragment is treated as staged-equivalent because it has already
   * passed the staged state.
   *
   * @internal Use AndDone.isFragmentStaged() instead.
   * @param fragmentName - Name of the fragment
   * @returns boolean
   */
  public isFragmentStaged(fragmentName: FragmentName): boolean {
    return this.lifecycle.isFragmentStaged(fragmentName);
  }

  /**
   * Update a fragment's status
   *
   * @param fragmentName - Name of the fragment
   * @param status - Fragment lifecycle status
   * @returns void
   */
  public setFragmentStatus(fragmentName: FragmentName, status: FragmentStatus): void {
    this.lifecycle.setFragmentStatus(fragmentName, status);
  }

  /**
   * Check if all processable fragments are staged.
   *
   * Submitted counts as staged-equivalent here so a fragment does not become
   * "unstaged" after successfully submitting.
   *
   * @internal Use AndDone.allFragmentsStaged() instead.
   * @return boolean
   */
  public allFragmentsStaged(): boolean {
    return this.lifecycle.allFragmentsStaged();
  }

  /**
   * Check if all processable fragments have been submitted
   *
   * @return boolean
   */
  public allFragmentsSubmitted(): boolean {
    return this.lifecycle.allFragmentsSubmitted();
  }

  /**
   * Get all fragments that are not staged.
   *
   * @internal Use AndDone.getUnstaggedFragments() instead.
   * @returns string[] - Array of fragment names that are not staged
   */
  public getUnstaggedFragments(): string[] {
    return this.lifecycle.getUnstaggedFragments();
  }

  /**
   * Send reset command to all fragments via orchestrator.
   *
   * @internal Use AndDone.reset() instead.
   * @returns void
   */
  public reset(): void {
    this.commands.reset();
  }

  /**
   * Submit handler - validates all fragments are ready before processing.
   *
   * @internal Use AndDone.submit() instead.
   * @returns boolean
   */
  public submit(): boolean {
    return this.commands.submit();
  }

  /**
   * Switch the active payment method and notify all fragments.
   *
   * @internal Use AndDone.updatePaymentMethod() instead.
   * @returns boolean
   */
  public updatePaymentMethod(paymentMethod: PaymentMethod): boolean {
    return this.dataState.paymentMethod.update(paymentMethod);
  }

  /**
   * Get all mounted fragment names
   *
   * @returns FragmentName[] - Array of fragment names
   */
  public getFragmentNames(): FragmentName[] {
    return this.registry.names();
  }

  /**
   * Unmount a fragment
   *
   * @param fragmentName - Name of the fragment to unmount
   * @returns boolean - True if unmounted successfully, false otherwise
   */
  public unmount(fragmentName: FragmentName): boolean {
    return this.mounting.unmount(fragmentName);
  }

  /**
   * Unmount all fragments
   *
   * @returns void
   */
  public unmountAll(): void {
    this.mounting.unmountAll();
  }

  /**
   * Clean up and remove event listeners
   *
   * @return void
   */
  public destroy(): void {
    this.unmountAll();
  }
}

// Freeze the prototype to prevent external code from redefining methods on
// the Fragments class to intercept or bypass fragment commands.
Object.freeze(Fragments.prototype);
